import { create } from 'zustand';
import type { ToastItem } from './types';

/**
 * Transient client-only UI state (NOT persisted, NOT server data).
 * - `sidebarCollapsed`: desktop full↔rail manual toggle (the topbar hamburger; our
 *   deliberate deviation from the prototype). Width-driven rail/drawer is derived in
 *   the view via `useViewportTier()`, NOT stored here.
 * - `drawerOpen`: mobile (≤760px) off-canvas sidebar open/closed.
 * - `toasts`: transient success/error notices.
 */
interface UiState {
  sidebarCollapsed: boolean;
  drawerOpen: boolean;
  toasts: ToastItem[];
}

interface UiActions {
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  addToast: (toast: Omit<ToastItem, 'id'>) => void;
  dismissToast: (id: string) => void;
}

let toastCounter = 0;

export const useUiStore = create<UiState & UiActions>()((set) => ({
  sidebarCollapsed: false,
  drawerOpen: false,
  toasts: [],

  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),

  openDrawer: () => set({ drawerOpen: true }),
  closeDrawer: () => set({ drawerOpen: false }),
  toggleDrawer: () => set((s) => ({ drawerOpen: !s.drawerOpen })),

  addToast: (toast) => set((s) => ({ toasts: [...s.toasts, { ...toast, id: String(++toastCounter) }] })),
  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));
